import React, { useState } from 'react';
import { FormInput } from '../components/FormInput';
import toast from 'react-hot-toast';

const SettingsForm = ({ settings, onSave }) => {
  const [formData, setFormData] = useState({
    siteTitle: settings?.siteTitle || '',
    siteDescription: settings?.siteDescription || '',
    tagline: settings?.tagline || '',
    companyName: settings?.companyName || '',
    contactEmail: settings?.contactEmail || '',
    phone: settings?.phone || '',
    address: settings?.address || '',
    logoUrl: settings?.logoUrl || '',
    headerBackgroundColor: settings?.headerBackgroundColor || '#FFFFFF',
    headerTextColor: settings?.headerTextColor || '#0A192F',
    footerBackgroundColor: settings?.footerBackgroundColor || '#0A192F',
    footerTextColor: settings?.footerTextColor || '#A0B4C8',
    footerText: settings?.footerText || '',
    copyrightText: settings?.copyrightText || '',
    socialLinks: settings?.socialLinks || JSON.stringify({
      linkedin: '#',
      twitter: '#',
      facebook: '#',
      instagram: '#'
    }, null, 2)
  });
  
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Validate social links JSON
      try {
        JSON.parse(formData.socialLinks);
      } catch (e) {
        toast.error('Invalid JSON format for social links');
        setLoading(false);
        return;
      }
      
      await onSave(formData);
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.message || 'Error saving settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLinksChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, socialLinks: value });
    
    // Validate JSON in real-time (optional)
    try {
      JSON.parse(value);
    } catch (e) {
      // Invalid JSON, but don't show error until submit
    }
  };

  return (
    <div style={{ 
      background: 'white', 
      borderRadius: '16px', 
      padding: '2rem', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <h2 style={{ 
        marginBottom: '1.5rem', 
        color: '#0A192F',
        fontSize: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <i className="fas fa-cog" style={{ color: '#2C7A7B' }}></i>
        Website Settings
      </h2>
      
      <p style={{ color: '#5A6E7F', marginBottom: '2rem' }}>
        Configure your website settings including contact information, address, colors, and more.
      </p>
      
      <form onSubmit={handleSubmit}>
        {/* General Settings */}
        <div style={{ 
          marginBottom: '2rem',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid #E2E8F0'
        }}>
          <h3 style={{ 
            marginBottom: '1rem', 
            color: '#0A192F',
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="fas fa-globe" style={{ color: '#2C7A7B' }}></i>
            General Settings
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
            gap: '1.5rem' 
          }}>
            <FormInput 
              label="Site Title" 
              name="siteTitle" 
              value={formData.siteTitle} 
              onChange={(e) => setFormData({ ...formData, siteTitle: e.target.value })} 
              placeholder="INTELIHEADS INTERNET Pvt. Ltd"
            />
            
            <FormInput 
              label="Site Description" 
              name="siteDescription" 
              value={formData.siteDescription} 
              onChange={(e) => setFormData({ ...formData, siteDescription: e.target.value })} 
              placeholder="Power Systems & Engineering Simulation"
            />
            
            <FormInput 
              label="Tagline" 
              name="tagline" 
              value={formData.tagline} 
              onChange={(e) => setFormData({ ...formData, tagline: e.target.value })} 
              placeholder="AI & Robotics in Education"
            />
            
            <FormInput 
              label="Company Name" 
              name="companyName" 
              value={formData.companyName} 
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} 
              placeholder="INTELIHEADS INTERNET Pvt. Ltd"
            />
          </div>
        </div>
        
        {/* Contact Information */}
        <div style={{ 
          marginBottom: '2rem',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid #E2E8F0'
        }}>
          <h3 style={{ 
            marginBottom: '1rem', 
            color: '#0A192F',
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="fas fa-address-card" style={{ color: '#2C7A7B' }}></i>
            Contact Information
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
            gap: '1.5rem' 
          }}>
            <FormInput 
              label="Contact Email" 
              name="contactEmail" 
              type="email" 
              value={formData.contactEmail} 
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} 
              placeholder="global@inteliheads.com"
            />
            
            <FormInput 
              label="Phone Number" 
              name="phone" 
              value={formData.phone} 
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
              placeholder="+91-8076842489"
            />
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 500,
                color: '#0A192F'
              }}>
                Address
              </label>
              <textarea 
                value={formData.address} 
                onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
                rows="3" 
                placeholder="Enter your full address"
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  border: '1px solid #E2E8F0', 
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontFamily: 'inherit'
                }} 
              />
              <small style={{ color: '#5A6E7F' }}>
                <i className="fas fa-info-circle"></i> This address will be displayed in the footer
              </small>
            </div>
            
            <FormInput 
              label="Logo URL" 
              name="logoUrl" 
              value={formData.logoUrl} 
              onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })} 
              placeholder="https://example.com/logo.png"
            />
          </div>
        </div>
        
        {/* Colors */}
        <div style={{ 
          marginBottom: '2rem',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid #E2E8F0'
        }}>
          <h3 style={{ 
            marginBottom: '1rem', 
            color: '#0A192F',
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="fas fa-palette" style={{ color: '#2C7A7B' }}></i>
            Colors & Styling
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '1.5rem' 
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 500,
                color: '#0A192F'
              }}>
                Header Background Color
              </label>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <input 
                  type="color" 
                  value={formData.headerBackgroundColor || '#FFFFFF'} 
                  onChange={(e) => setFormData({ ...formData, headerBackgroundColor: e.target.value })} 
                  style={{ 
                    width: '60px', 
                    height: '45px', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px', 
                    padding: '0.25rem',
                    cursor: 'pointer'
                  }} 
                />
                <input 
                  type="text" 
                  value={formData.headerBackgroundColor || '#FFFFFF'} 
                  onChange={(e) => setFormData({ ...formData, headerBackgroundColor: e.target.value })} 
                  style={{ 
                    flex: 1,
                    padding: '0.5rem', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px',
                    fontFamily: 'monospace'
                  }} 
                />
              </div>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 500,
                color: '#0A192F'
              }}>
                Header Text Color
              </label>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <input 
                  type="color" 
                  value={formData.headerTextColor || '#0A192F'} 
                  onChange={(e) => setFormData({ ...formData, headerTextColor: e.target.value })} 
                  style={{ 
                    width: '60px', 
                    height: '45px', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px', 
                    padding: '0.25rem',
                    cursor: 'pointer'
                  }} 
                />
                <input 
                  type="text" 
                  value={formData.headerTextColor || '#0A192F'} 
                  onChange={(e) => setFormData({ ...formData, headerTextColor: e.target.value })} 
                  style={{ 
                    flex: 1,
                    padding: '0.5rem', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px',
                    fontFamily: 'monospace'
                  }} 
                />
              </div>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 500,
                color: '#0A192F'
              }}>
                Footer Background Color
              </label>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <input 
                  type="color" 
                  value={formData.footerBackgroundColor || '#0A192F'} 
                  onChange={(e) => setFormData({ ...formData, footerBackgroundColor: e.target.value })} 
                  style={{ 
                    width: '60px', 
                    height: '45px', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px', 
                    padding: '0.25rem',
                    cursor: 'pointer'
                  }} 
                />
                <input 
                  type="text" 
                  value={formData.footerBackgroundColor || '#0A192F'} 
                  onChange={(e) => setFormData({ ...formData, footerBackgroundColor: e.target.value })} 
                  style={{ 
                    flex: 1,
                    padding: '0.5rem', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px',
                    fontFamily: 'monospace'
                  }} 
                />
              </div>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 500,
                color: '#0A192F'
              }}>
                Footer Text Color
              </label>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <input 
                  type="color" 
                  value={formData.footerTextColor || '#A0B4C8'} 
                  onChange={(e) => setFormData({ ...formData, footerTextColor: e.target.value })} 
                  style={{ 
                    width: '60px', 
                    height: '45px', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px', 
                    padding: '0.25rem',
                    cursor: 'pointer'
                  }} 
                />
                <input 
                  type="text" 
                  value={formData.footerTextColor || '#A0B4C8'} 
                  onChange={(e) => setFormData({ ...formData, footerTextColor: e.target.value })} 
                  style={{ 
                    flex: 1,
                    padding: '0.5rem', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px',
                    fontFamily: 'monospace'
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer Content */}
        <div style={{ 
          marginBottom: '2rem',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid #E2E8F0'
        }}>
          <h3 style={{ 
            marginBottom: '1rem', 
            color: '#0A192F',
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="fas fa-file-alt" style={{ color: '#2C7A7B' }}></i>
            Footer Content
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
            gap: '1.5rem' 
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 500,
                color: '#0A192F'
              }}>
                Footer Text
              </label>
              <textarea 
                value={formData.footerText} 
                onChange={(e) => setFormData({ ...formData, footerText: e.target.value })} 
                rows="3" 
                placeholder="Enter footer description text"
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  border: '1px solid #E2E8F0', 
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontFamily: 'inherit'
                }} 
              />
            </div>
            
            <FormInput 
              label="Copyright Text" 
              name="copyrightText" 
              value={formData.copyrightText} 
              onChange={(e) => setFormData({ ...formData, copyrightText: e.target.value })} 
              placeholder="© 2026 INTELIHEADS INTERNET Pvt. Ltd — Innovating Today & Empowering Tomorrow."
            />
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 500,
                color: '#0A192F'
              }}>
                Social Links (JSON)
              </label>
              <textarea 
                value={formData.socialLinks} 
                onChange={handleSocialLinksChange} 
                rows="4" 
                placeholder='{"linkedin":"#","twitter":"#","facebook":"#","instagram":"#"}'
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  border: '1px solid #E2E8F0', 
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontFamily: 'monospace'
                }} 
              />
              <small style={{ color: '#5A6E7F' }}>
                <i className="fas fa-info-circle"></i> Enter valid JSON format for social media links
              </small>
            </div>
          </div>
        </div>
        
        {/* Submit Button */}
        <div style={{ 
          marginTop: '2rem', 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '1rem'
        }}>
          <button 
            type="button"
            onClick={() => {
              // Reset form to initial settings
              setFormData({
                siteTitle: settings?.siteTitle || '',
                siteDescription: settings?.siteDescription || '',
                tagline: settings?.tagline || '',
                companyName: settings?.companyName || '',
                contactEmail: settings?.contactEmail || '',
                phone: settings?.phone || '',
                address: settings?.address || '',
                logoUrl: settings?.logoUrl || '',
                headerBackgroundColor: settings?.headerBackgroundColor || '#FFFFFF',
                headerTextColor: settings?.headerTextColor || '#0A192F',
                footerBackgroundColor: settings?.footerBackgroundColor || '#0A192F',
                footerTextColor: settings?.footerTextColor || '#A0B4C8',
                footerText: settings?.footerText || '',
                copyrightText: settings?.copyrightText || '',
                socialLinks: settings?.socialLinks || JSON.stringify({
                  linkedin: '#',
                  twitter: '#',
                  facebook: '#',
                  instagram: '#'
                }, null, 2)
              });
              toast.info('Form reset to saved settings');
            }}
            style={{ 
              padding: '0.75rem 1.5rem', 
              background: 'transparent', 
              color: '#5A6E7F',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#F7F9FC';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <i className="fas fa-undo"></i> Reset
          </button>
          
          <button 
            type="submit" 
            disabled={loading} 
            style={{ 
              padding: '0.75rem 2rem', 
              background: '#2C7A7B', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontWeight: 600,
              transition: 'all 0.2s',
              opacity: loading ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.background = '#236B6C';
            }}
            onMouseLeave={(e) => {
              if (!loading) e.currentTarget.style.background = '#2C7A7B';
            }}
          >
            {loading ? (
              <><i className="fas fa-spinner fa-spin"></i> Saving...</>
            ) : (
              <><i className="fas fa-save"></i> Save All Settings</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettingsForm;